import React from 'react';
import { cn } from 'lib/utils';
import { Button, Icon, Link } from 'ui';
import { type ResourceGridSection } from './index';

type ResourceCardProps = ResourceGridSection['items'][0] & {
  layout: ResourceGridSection['itemsLayout'];
};

const ResourceCard = ({
  layout,
  eyebrow,
  title,
  link,
  publication,
  publicationYear,
}: ResourceCardProps) => {
  const isExternalLink = link.target === '_blank';

  return (
    <article className="relative bg-graphite-950/30 p-6 rounded-lg flex flex-col gap-6 h-full">
      {/* Eyebrow & Title */}
      <div
        className={cn('grow flex flex-col items-start', {
          'gap-6': layout === 'blogs',
          'gap-4': layout === 'news',
        })}
      >
        <div
          className={cn('font-mono uppercase', {
            'text-eyebrow-sm py-2 px-3 bg-graphite-600 rounded-md':
              layout === 'blogs',
            'text-eyebrow': layout === 'news',
          })}
        >
          {eyebrow}
        </div>

        <h4 className="max-lg:text-p1 lg:text-h5 line-clamp-4 lg:line-clamp-3">
          {title}
        </h4>
      </div>

      {/* Footer for Blogs Layout */}
      {layout === 'blogs' && (
        <footer className="flex justify-between items-center gap-4">
          {publication && (
            <p className="text-p1-b flex flex-col">
              <span className="italic">{publication}</span>
              {publicationYear && <span>{publicationYear}</span>}
            </p>
          )}

          <div className="grow h-12 min-w-12 flex justify-end">
            <Button
              href={link.href}
              target={link.target}
              className="size-12 after:absolute after:inset-0 p-0"
              variant="secondary"
              aria-label={link.text}
              trackingProps={link.trackingProps}
            >
              <Icon
                name={
                  isExternalLink
                    ? 'external-link-arrow'
                    : 'internal-link-arrow-right'
                }
                className={cn('h-auto shrink-0', {
                  'w-4': !isExternalLink,
                  'w-3.5': isExternalLink,
                })}
              />
            </Button>
          </div>
        </footer>
      )}

      {/* Footer for News Layout */}
      {layout === 'news' && (
        <footer className="flex justify-between items-center gap-4 border-t border-clay-50/20 pt-6">
          <Link
            className="flex items-center gap-2 text-p1-b font-bold no-underline after:absolute after:inset-0"
            {...link}
          >
            <span className="line-clamp-1">{link.text}</span>
            <Icon
              name={
                isExternalLink
                  ? 'external-link-arrow'
                  : 'internal-link-arrow-right'
              }
              className={cn('h-auto shrink-0', {
                'w-3.5': !isExternalLink,
                'w-3': isExternalLink,
              })}
            />
          </Link>
        </footer>
      )}
    </article>
  );
};

export default ResourceCard;
